import { FC, useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Link,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector, RootState } from '../../store';
import { documentsGetAll, documentDelete, openDocumentDialog, closeDocumentDrawer } from '../../slices/documentSlice';
import { getActiveElement } from '../../utils/active-element';
import { screenLabelFromParentType } from '../../hooks/use-screen-context';
import { Trash } from '../../icons/trash';
import { Download } from '../../icons/download';
import apiService from '../../services/api.service';
import { useTranslation } from 'react-i18next';

// Right-side panel opened from a review page's "Uploads" button. Lets the user
// upload a new document (reusing the document dialog, which auto-tags it with the
// current page) and lists the documents already attached to this page.
export const DocumentsDrawer: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { documentDrawerOpen, documents, documentStatus } = useSelector((s: RootState) => s.document);
  const { parentType, parentId } = getActiveElement(location.pathname);
  const [docToDelete, setDocToDelete] = useState<any>(null);
  // null = the current page; otherwise the parent_type of another screen whose
  // uploads the user chose to browse from here.
  const [selectedParentType, setSelectedParentType] = useState<string | null>(null);

  const handleConfirmDelete = () => {
    if (docToDelete) {
      dispatch(documentDelete(docToDelete.id) as any);
    }
    setDocToDelete(null);
  };

  useEffect(() => {
    if (documentDrawerOpen) {
      dispatch(documentsGetAll() as any);
      // Reopening the drawer always starts back on the current page.
      setSelectedParentType(null);
    }
  }, [documentDrawerOpen, dispatch]);

  // Refresh the list after a successful upload while the drawer is open.
  useEffect(() => {
    if (documentDrawerOpen && documentStatus === 'success') {
      dispatch(documentsGetAll() as any);
    }
  }, [documentStatus]);

  // Documents attached to the current page (matched on parent_type, and
  // parent_id when the page is scoped to a specific item).
  const pageDocs = (documents || []).filter(
    (d: any) =>
      d.parent_type === parentType && (parentId ? String(d.parent_id) === String(parentId) : true)
  );

  const currentParentType = parentType;
  const effectiveParentType = selectedParentType ?? currentParentType;

  // Screens the selector can switch to: every page that already has at least one
  // document, plus the current page so the selector is always initialized on it
  // (even before it has any upload). Built from the documents already fetched —
  // no need to enumerate every route in the app.
  const screenOptions = Array.from(
    new Set(
      (documents || [])
        .map((d: any) => d.parent_type)
        .filter(Boolean)
        .concat(currentParentType ? [currentParentType] : [])
    )
  ).sort();

  // The list shows the current page (parent_type + parent_id scoped) by default,
  // or every document of the selected page's parent_type once the user switches
  // the selector to another screen.
  const displayedDocs =
    selectedParentType === null
      ? pageDocs
      : (documents || []).filter((d: any) => d.parent_type === selectedParentType);

  // Falls back to "Whole Project" rather than the feature name: this fills the
  // {{screen}} slot of "List of uploads for …", where "Uploads" would read as
  // "List of uploads for Uploads".
  const screenLabel = screenLabelFromParentType(effectiveParentType) || t('whole_project');

  return (
    <Drawer
      anchor="right"
      open={documentDrawerOpen}
      onClose={() => dispatch(closeDocumentDrawer())}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, backgroundColor: 'background.default' } }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('uploads')}
          </Typography>
          <IconButton onClick={() => dispatch(closeDocumentDrawer())} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Button variant="contained" fullWidth onClick={() => dispatch(openDocumentDialog({}))}>
          {t('add_file_or_url')}
        </Button>

        {/* Plain anchor: the endpoint streams a zip and the session cookie rides
            along on a top-level GET, same as the per-file download links. */}
        <Button
          variant="outlined"
          fullWidth
          startIcon={<Download fontSize="small" />}
          component="a"
          href={`${apiService.defaults.baseURL}/documents/export`}
          sx={{ mt: 1 }}
        >
          {t('export_all_uploads')}
        </Button>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('list_of_uploads_for', { screen: screenLabel })}
          </Typography>
          {effectiveParentType && screenOptions.length > 1 && (
            <TextField
              select
              size="small"
              fullWidth
              value={effectiveParentType}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedParentType(value === currentParentType ? null : value);
              }}
            >
              {screenOptions.map((pt) => (
                <MenuItem key={pt} value={pt}>
                  {screenLabelFromParentType(pt) || pt}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {displayedDocs.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {t('no_documents')}
            </Typography>
          ) : (
            <List dense>
              {displayedDocs.map((d: any) => {
                const href = d.url || d.file_path;
                const isLink = Boolean(d.url);
                return (
                  <ListItem key={d.id} divider disableGutters>
                    <ListItemText
                      primary={
                        href ? (
                          <Link href={href} target="_blank" rel="noreferrer noopener">
                            {d.title}
                          </Link>
                        ) : (
                          d.title
                        )
                      }
                      secondary={d.description}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color={isLink ? 'primary' : 'default'}
                      label={isLink ? t('link') : t('file')}
                    />
                    {!isLink && d.file_path && (
                      <Tooltip title={t('download')}>
                        <IconButton
                          edge="end"
                          size="small"
                          component="a"
                          href={d.file_path}
                          target="_blank"
                          rel="noreferrer noopener"
                          sx={{ ml: 0.5 }}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('delete')}>
                      <IconButton edge="end" size="small" color="error" onClick={() => setDocToDelete(d)} sx={{ ml: 0.5 }}>
                        <Trash fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Dialog open={Boolean(docToDelete)} onClose={() => setDocToDelete(null)}>
          <DialogTitle>{t('delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {docToDelete && (
                <>
                  {t('confirm_delete_document')} <strong>{docToDelete.title}</strong>?
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDocToDelete(null)}>{t('cancel')}</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Drawer>
  );
};
